// DraggableGrid.tsx
import React, { useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  alpha
} from '@mui/material';
import { DragDropContext, Draggable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid';
import StrictModeDroppable from './StrictModeDroppable';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Column, DraggableGridProps, Row } from '../../interfaces/Components/DraggableGridProps';

/**
 * Reordena elementos en base a índices de inicio y destino
 * y actualiza la propiedad `sesion` según nueva posición.
 */
const reorder = (list: Row[], start: number, end: number): Row[] => {
  const result = Array.isArray(list) ? [...list] : [];
  const [removed] = result.splice(start, 1);
  result.splice(end, 0, removed);
  return result.map((item, idx) => ({ ...item, sesion: idx + 1 }));
};

/**
 * Crea una nueva fila en base a columnas y filas existentes.
 */
const createNewRow = (columns: Column[], rows: Row[]): Row => {
  const semana = Array.isArray(rows) && rows.length > 0 ? rows[0].semana : '';
  const base: any = { id: uuidv4(), sesion: rows.length + 1, semana, actividades: [] };
  return columns.reduce((acc, col) => ({ ...acc, [col.accessor]: '' }), base);
};

/**
 * Renderiza las celdas de encabezado
 */
const renderHeaderCells = (columns: Column[]) =>
  columns.map(col => (
    <TableCell
      key={col.accessor}
      sx={{ backgroundColor: alpha('#90AFC5', 1), color: '#FFF', fontFamily: 'Montserrat' }}
    >
      {col.Header}
    </TableCell>
  ));

/**
 * Renderiza las celdas de contenido para una fila
 */
interface RowCellsProps {
  row: Row;
  columns: Column[];
  onSave: (i: number, key: string, val: string) => void;
  index: number;
}
const RowCells: React.FC<RowCellsProps> = ({ row, columns, onSave, index }) => (
  <>
    {columns.map(col => (
      col.accessor === 'sesion' ? (
        <TableCell key={col.accessor} sx={{ fontFamily: 'Montserrat' }}>
          {row.sesion}
        </TableCell>
      ) : (
        <TableCell key={col.accessor} sx={{ fontFamily: 'Montserrat' }}>
          <TextField
            fullWidth
            value={row[col.accessor]}
            onChange={e => onSave(index, col.accessor, e.target.value)}
            InputProps={{
              sx: {
                borderRadius: 2,
                fontFamily: 'Montserrat',
                '&:before': { borderBottomColor: '#005559' },
                '&:after': { borderBottomColor: '#005559' }
              }
            }}
          />
        </TableCell>
      )
    ))}
  </>
);

/**
 * Fila draggable memoizada
 */
interface DraggableRowProps extends RowCellsProps {
  onRemove: (i: number) => void;
  row: Row;
}
const DraggableRow: React.FC<DraggableRowProps> = React.memo(({ row, index, columns, onSave, onRemove }) => (
  <Draggable key={row.id} draggableId={row.id} index={index}>
    {provided => (
      <TableRow
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        sx={{ backgroundColor: index % 2 === 0 ? '#FFF' : 'rgba(144,175,197,0.3)' }}
      >
        <TableCell><DragIndicatorIcon /></TableCell>
        <RowCells row={row} columns={columns} onSave={onSave} index={index} />
        <TableCell sx={{ textAlign: 'center' }}>
          <IconButton onClick={() => onRemove(index)}>
            <RemoveCircleIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    )}
  </Draggable>
));

const DraggableGrid: React.FC<DraggableGridProps> = ({ columns, rows, setRows }) => {
  // Asegurar que rows sea siempre array, memoizado para estabilidad en hooks
  const safeRows = useMemo<Row[]>(() => (Array.isArray(rows) ? rows : []), [rows]);

  const handleDragEnd = useCallback((res: DropResult) => {
    if (!res.destination) return;
    setRows(reorder(safeRows, res.source.index, res.destination.index));
  }, [safeRows, setRows]);

  const handleSave = useCallback((i: number, key: string, val: string) => {
    const updated = safeRows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    setRows(updated);
  }, [safeRows, setRows]);

  const handleAdd = useCallback(() => {
    const newRow = createNewRow(columns, safeRows);
    const combined = [...safeRows, newRow].map((r, idx) => ({ ...r, sesion: idx + 1 }));
    setRows(combined);
  }, [columns, safeRows, setRows]);

  const handleRemove = useCallback((i: number) => {
    const filtered = safeRows.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, sesion: idx + 1 }));
    setRows(filtered);
  }, [safeRows, setRows]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <StrictModeDroppable droppableId="dg">
        {(provided: DroppableProvided) => (
          <TableContainer component={Paper} ref={provided.innerRef} {...provided.droppableProps}>
            <Table sx={{ minWidth: 650, width: '100%' }} size="small" aria-label="draggable table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: alpha('#90AFC5', 1), color: '#FFF', fontFamily: 'Montserrat' }} />
                  {renderHeaderCells(columns)}
                  <TableCell sx={{ backgroundColor: alpha('#90AFC5', 1), textAlign: 'center', color: '#FFF' }}>
                    <IconButton onClick={handleAdd}><AddCircleIcon /></IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ fontFamily: 'Montserrat' }}>
                {safeRows.map((row, idx) => (
                  <DraggableRow
                    key={row.id}
                    row={row}
                    index={idx}
                    columns={columns}
                    onSave={handleSave}
                    onRemove={handleRemove}
                  />
                ))}
                {provided.placeholder}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

export default DraggableGrid;
